import { HairColors, PantsColors } from "game-common/character/character"
import { EmberEquipment, emberEquipmentOf } from "game-common/ember_sprite/generate"
import { emberSpriteFor } from "game-common/ember_sprite/humanoids"
import { generateItems } from "game-common/item/item"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { Appearance, Entity, Inventory, pctChance } from "game-common/models"
import { degrees_to_radians, randomOneOf } from "game-common/util"

import { NONE, SectionKind, TraitId, TraitOption, TraitView, applyTraitChoice, describeTrait, reconcileForSex, traitsFor } from "./traits"

// The character-creation page as data: the draft character, which way the figure faces, whether the
// panel shows every trait or the full grid of hairstyles, and the choice the pointer is over. Everything
// the panel draws is derived from it, and every action the page offers is a method on it.
//
// Pure: no pixi, no renderer. The page paints it and routes input to it.

/** The eight ways the figure can face, in turning order: each step right turns its near side to the right. */
export const FACINGS = ["front", "frontright", "right", "backright", "back", "backleft", "left", "frontleft"] as const
export type Facing = (typeof FACINGS)[number]
/** The figure starts three-quarters toward the viewer, facing right, as the old forge did. */
export const DEFAULT_FACING = 1
/** How far a drag travels, in art pixels, to turn the figure one facing. */
export const DRAG_STEP = 12
/** How many hairstyles (and beards) the panel's row shows round the current one. */
export const HAIR_WINDOW = 7
/** How often Roll look leaves a man clean-shaven; otherwise he gets any beard, each as likely. */
export const ROLL_CLEAN_SHAVEN = 0.4

export type SheetMode = "traits" | "hairGrid"

/**
 * On a phone, each panel shows one trait at a time: these on the left of the figure (a man's beard after his
 * hair; leftTabs), these on the right.
 */
export const LEFT_TABS = ["sex", "skin", "hair", "beard"] as const
export const RIGHT_TABS = ["shirt", "pants", "shoes"] as const
export type LeftTab = (typeof LEFT_TABS)[number]
export type RightTab = (typeof RIGHT_TABS)[number]

export interface HairPage {
    page: number
    pages: number
    options: SheetOption[]
}

/** One choice on the panel: a value of one section of one trait. */
export interface Choice {
    trait: TraitId
    kind: SectionKind
    value: string
}

export const sameChoice = (a?: Choice, b?: Choice): boolean =>
    !!a && !!b && a.trait === b.trait && a.kind === b.kind && a.value === b.value

export interface SheetOption extends TraitOption {
    selected: boolean
}

export interface SheetSection {
    kind: SectionKind
    title: string
    options: SheetOption[]
}

export interface SheetRow {
    id: TraitId
    label: string
    /** The trait's line: the choice made, or the one the pointer is over. */
    line: string
    pointing: boolean
    style?: SheetSection
    color?: SheetSection
}

/** How light a colour looks, for running skin tones from lightest to darkest. */
export const lightness = (color: number): number =>
    0.2126 * ((color >> 16) & 0xff) + 0.7152 * ((color >> 8) & 0xff) + 0.0722 * (color & 0xff)

const sectionOf = (view: TraitView, kind: SectionKind): SheetSection | undefined => {
    const section = view.sections.find(s => s.kind === kind)
    if (!section) {
        return undefined
    }
    let options = section.options.map(option => ({ ...option, selected: option.value === section.selected }))
    if (view.id === "skin" && kind === "color") {
        options = options.slice().sort((a, b) => lightness(b.swatch ?? 0) - lightness(a.swatch ?? 0))
    }
    return { kind, title: section.title, options }
}

const labelOf = (section: SheetSection | undefined, value: string): string | undefined =>
    section?.options.find(option => option.value === value)?.label

/**
 * The draft a new character starts from. Deep enough that editing it can't reach back into the live
 * player (a shallow copy shares the items array, so a new shirt would land on the player mid-creation).
 */
export const draftFrom = (player: Entity): Entity => {
    const entity = new Entity()
    entity.movement.angle = degrees_to_radians(45)
    entity.weapon = { weaponType: "none" }
    entity.appearance = { ...(player.appearance || randomAppearanceFor(undefined, "human", "male")) }
    entity.inventory = {
        items: (player.inventory?.items || []).map(item => ({
            ...item,
            itemTypeMeta: item.itemTypeMeta ? { ...item.itemTypeMeta } : undefined,
        })),
        abilities: { ...(player.inventory?.abilities || {}) },
        lastUpdateTs: 0,
    }

    const appearance = entity.appearance
    appearance.race = "human"
    appearance.sex = "male"
    appearance.bodyColor = "light"
    appearance.hair = "high_and_tight"
    appearance.hairColor = randomOneOf(HairColors.high_and_tight)
    // Eyes aren't a choice, but an appearance that arrived without an eye layer still gets the default.
    appearance.eyes = appearance.eyes || "normal"
    appearance.eyesColor = appearance.eyesColor || "brown"
    appearance.noseColor = appearance.bodyColor
    applyTraitChoice(entity, "shirt", "style", "longsleeve")
    applyTraitChoice(entity, "shirt", "color", "gray")
    applyTraitChoice(entity, "pants", "style", "pantaloons")
    applyTraitChoice(entity, "pants", "color", randomOneOf(PantsColors.pantaloons))
    applyTraitChoice(entity, "shoes", "style", "none")
    reconcileForSex(entity)
    return entity
}

/** HAIR_WINDOW options round the selected one, in the middle, wrapping at both ends; all of them if they fit. */
const windowOf = (options: SheetOption[]): SheetOption[] => {
    const n = options.length
    if (n <= HAIR_WINDOW) {
        return options
    }
    const at = Math.max(0, options.findIndex(option => option.selected))
    const half = Math.floor(HAIR_WINDOW / 2)
    return Array.from({ length: HAIR_WINDOW }, (_, k) => options[(at + k - half + n) % n])
}

/** A copy of a draft for trying an option on: its own appearance and items, nothing shared. */
export const copyDraft = (draft: Entity): Entity => {
    const entity = new Entity()
    entity.movement.angle = draft.movement.angle
    entity.weapon = { weaponType: "none" }
    entity.appearance = { ...draft.appearance }
    entity.inventory = {
        items: draft.inventory.items.map(item => ({ ...item, itemTypeMeta: item.itemTypeMeta ? { ...item.itemTypeMeta } : undefined })),
        abilities: { ...draft.inventory.abilities },
        lastUpdateTs: 0,
    }
    return entity
}

/** The Ember body a draft is drawn as. Every human has one; the fallback only guards a bad appearance. */
export const spriteOf = (draft: Entity): string => emberSpriteFor(draft.appearance) ?? "realist"

export class CreationSheet {
    readonly draft: Entity
    facing = DEFAULT_FACING
    mode: SheetMode = "traits"
    pointed?: Choice
    /** The phone's two panels: which trait each shows. */
    tabs: { left: LeftTab; right: RightTab } = { left: "sex", right: "shirt" }
    // The phone's hair grid: which page, or none chosen yet (it then opens on the current style's).
    private hairPage?: number
    // What a drag has travelled since it last turned the figure, so a slow drag still turns.
    private dragCarry = 0

    constructor(player: Entity) {
        this.draft = draftFrom(player)
    }

    getAppearance = (): Appearance => this.draft.appearance
    getInventory = (): Inventory => this.draft.inventory

    get facingName(): Facing {
        return FACINGS[this.facing]
    }

    sprite = (): string => spriteOf(this.draft)
    equipment = (): EmberEquipment => emberEquipmentOf(this.draft)

    /** Every trait this build sets, in the panel's order, with its sections and its line. */
    rows = (): SheetRow[] =>
        traitsFor(this.draft.appearance.sex).map(id => {
            const view = describeTrait(this.draft, id)
            const style = sectionOf(view, "style")
            const color = sectionOf(view, "color")
            const pointed = this.pointed?.trait === id ? this.pointed : undefined
            const pointedName = pointed && labelOf(pointed.kind === "style" ? style : color, pointed.value)
            const chosen = [style && labelOf(style, style.options.find(o => o.selected)?.value ?? ""), color && labelOf(color, color.options.find(o => o.selected)?.value ?? "")]
            return {
                id,
                label: view.label,
                line: pointedName ?? chosen.filter(Boolean).join(" · "),
                pointing: !!pointedName,
                style,
                color,
            }
        })

    row = (id: TraitId): SheetRow => this.rows().find(row => row.id === id)

    /** The hairstyles the row shows: the current one in the middle, the rest round it, wrapping at both ends. */
    hairWindow = (): SheetOption[] => windowOf(this.row("hair").style.options)

    /** A man's beards as his row shows them, like the hair row's; none for a build that has no beard. */
    beardWindow = (): SheetOption[] => (this.row("beard") ? windowOf(this.row("beard").style.options) : [])

    /** Every hairstyle this character can wear, for the grid. */
    gridOptions = (): SheetOption[] => this.row("hair").style.options

    pick = (choice: Choice) => {
        applyTraitChoice(this.draft, choice.trait, choice.kind, choice.value)
        // A Beard tab left open when the build loses its beard turns back to Hair.
        if (!this.leftTabs().includes(this.tabs.left)) this.tabs.left = "hair"
    }

    /** The left panel's tabs on a phone: the traits of LEFT_TABS this build sets. */
    leftTabs = (): LeftTab[] => {
        const traits = traitsFor(this.draft.appearance.sex)
        return LEFT_TABS.filter(tab => traits.includes(tab))
    }

    /** One hairstyle on, or back, from the current one. */
    stepHair = (direction: number) => {
        const options = this.gridOptions()
        const at = Math.max(0, options.findIndex(option => option.selected))
        const next = options[(at + direction + options.length) % options.length]
        this.pick({ trait: "hair", kind: "style", value: next.value })
    }

    /** One beard on, or back, from the current one, wrapping at both ends. */
    stepBeard = (direction: number) => {
        const row = this.row("beard")
        if (!row) return
        const options = row.style.options
        const at = Math.max(0, options.findIndex(option => option.selected))
        const next = options[(at + Math.sign(direction) + options.length) % options.length]
        this.pick({ trait: "beard", kind: "style", value: next.value })
    }

    /** Show a trait on its side's phone panel. Opening Hair turns to the page holding the current style. */
    selectTab = (trait: TraitId) => {
        if ((this.leftTabs() as string[]).includes(trait)) {
            this.tabs.left = trait as LeftTab
            if (trait === "hair") this.hairPage = undefined
        } else if ((RIGHT_TABS as readonly string[]).includes(trait)) {
            this.tabs.right = trait as RightTab
        }
    }

    /** The phone's hair grid, `perPage` styles a page. */
    hairPages = (perPage: number): HairPage => {
        const options = this.gridOptions()
        const size = Math.max(1, perPage)
        const pages = Math.max(1, Math.ceil(options.length / size))
        if (this.hairPage === undefined) {
            this.hairPage = Math.floor(Math.max(0, options.findIndex(option => option.selected)) / size)
        }
        const page = Math.min(pages - 1, Math.max(0, this.hairPage))
        this.hairPage = page
        return { page, pages, options: options.slice(page * size, page * size + size) }
    }

    /** One page on, or back, wrapping at both ends. */
    turnHairPage = (direction: number, perPage: number) => {
        const { page, pages } = this.hairPages(perPage)
        this.hairPage = (page + Math.sign(direction) + pages) % pages
    }

    openGrid = () => {
        this.mode = "hairGrid"
    }

    closeGrid = () => {
        this.mode = "traits"
    }

    point = (choice?: Choice) => {
        this.pointed = choice
    }

    /** One facing round: 1 swings the near side to the right, as the right-hand button and a rightward drag do. */
    turn = (direction: number) => {
        this.facing = (this.facing + Math.sign(direction) + FACINGS.length) % FACINGS.length
    }

    /** A drag moved `dx` art pixels: turn a facing for every DRAG_STEP of it. Returns the facings turned. */
    drag = (dx: number): number => {
        this.dragCarry += dx
        let turned = 0
        while (Math.abs(this.dragCarry) >= DRAG_STEP) {
            const direction = Math.sign(this.dragCarry)
            this.turn(direction)
            this.dragCarry -= direction * DRAG_STEP
            turned += direction
        }
        return turned
    }

    endDrag = () => {
        this.dragCarry = 0
    }

    /** A random skin, hairstyle and hair colour for this build, and a man a random beard; the clothes stay on. */
    rollLook = (random: () => number = Math.random) => {
        const appearance = randomAppearanceFor(this.draft, "human", this.draft.appearance.sex)
        appearance.inventoryAppearanceHash = this.draft.appearance.inventoryAppearanceHash
        this.draft.appearance = appearance
        appearance.eyes = appearance.eyes || "normal"
        appearance.noseColor = appearance.bodyColor
        reconcileForSex(this.draft)
        const beards = this.row("beard")?.style.options.map(option => option.value).filter(value => value !== NONE)
        if (beards?.length && random() >= ROLL_CLEAN_SHAVEN) {
            this.pick({ trait: "beard", kind: "style", value: beards[Math.floor(random() * beards.length) % beards.length] })
        }
    }

    /** A random shirt, legs and feet this build can wear; the body and hair stay. */
    rollOutfit = () => {
        const sex = this.draft.appearance.sex
        this.draft.inventory.items = generateItems(sex, {
            pants: true,
            shirt: sex === "male" ? pctChance(60) : true,
            shoe: true,
            weapon: true,
        })
        reconcileForSex(this.draft)
    }
}
